import { useState } from "react";

import { previewImport, commitImport, type CsvImportPreviewResult, type CsvImportCommitResult } from "@/api/physicalRecordsApi";

export default function AdminPhysicalRecordsMigrationImport() {
  const [recordType, setRecordType] = useState<"Box" | "File">("Box");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CsvImportPreviewResult | null>(null);
  const [commitResult, setCommitResult] = useState<CsvImportCommitResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onPreview() {
    if (!file) {
      setErr("Choose a CSV file first.");
      return;
    }
    try {
      setLoading(true);
      setErr(null);
      setCommitResult(null);
      const result = await previewImport(file, recordType);
      setPreview(result);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Preview failed.");
    } finally {
      setLoading(false);
    }
  }

  async function onCommit() {
    if (!file) return;
    try {
      setLoading(true);
      setErr(null);
      const result = await commitImport(file, recordType);
      setCommitResult(result);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Import failed.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container py-4">
      <div className="mb-3">
        <h1 className="h4 mb-1">Migrate Legacy InfoLink Data</h1>
        <div className="text-muted">
          Generic CSV import — column mapping is a placeholder until the exact InfoLink export
          schema is confirmed. Always preview before committing.
        </div>
      </div>

      <div className="alert alert-info">
        Expected columns —{" "}
        <strong>Box:</strong> BoxNumber, BoxName, LocationName, DepartmentName, ContentStartDate, ContentEndDate,
        Description, Keywords, RetentionLabelName, OffsiteId, CreateUser, CreateDate.{" "}
        <strong>File:</strong> FileNumber, FileName, LocationName, DepartmentName, Keywords, ContentStartDate,
        ContentEndDate, RetentionLabelName, ContractParties, OffsiteId, OffsiteStorageId, BoxNumber, CreateUser, CreateDate.
        Location/Department/Retention Label values must already exist in the admin lookup lists.
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      <div className="card mb-3">
        <div className="card-body d-flex gap-2 align-items-end">
          <div>
            <label className="form-label">Record Type</label>
            <select className="form-select" value={recordType} onChange={(e) => setRecordType(e.target.value as "Box" | "File")}>
              <option value="Box">Box</option>
              <option value="File">File</option>
            </select>
          </div>
          <div className="flex-grow-1">
            <label className="form-label">CSV File</label>
            <input type="file" accept=".csv" className="form-control" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <button className="btn btn-outline-primary" disabled={loading} onClick={onPreview}>
            Preview
          </button>
          <button className="btn btn-primary" disabled={loading || !preview || preview.validRows === 0} onClick={onCommit}>
            Commit Import
          </button>
        </div>
      </div>

      {preview && (
        <div className="mb-3">
          <div className="mb-2">
            {preview.totalRows} row(s) — <span className="text-success">{preview.validRows} valid</span>,{" "}
            <span className="text-danger">{preview.invalidRows} invalid</span>
          </div>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Row</th>
                <th>Valid</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r) => (
                <tr key={r.rowNumber} className={r.isValid ? "" : "table-danger"}>
                  <td>{r.rowNumber}</td>
                  <td>{r.isValid ? "Yes" : "No"}</td>
                  <td>{r.error ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {commitResult && (
        <div className="alert alert-success">
          Imported {commitResult.importedCount}, skipped {commitResult.skippedCount}.
          {commitResult.errors.length > 0 && (
            <ul className="mb-0 mt-2">
              {commitResult.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
